import React, { useState, useEffect } from "react";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";

// Utility formatters
const fmt = (val, decimals = 2) =>
    val !== undefined && val !== null && !isNaN(val) ? Number(val).toFixed(decimals) : "";

const percent = (num, total) =>
    total && !isNaN(num) ? `${((num / total) * 100).toFixed(2)}%` : "0%";

// Reusable editable input
const EditableInput = ({ value, name, onChange, keyName, width = "100%" }) => {
    const handleChange = (e) => {
        const newVal = e.target.value;
        // Allow empty, integers, decimals, partial decimal typing
        if (/^\d*\.?\d*$/.test(newVal) || newVal === "") {
            // Pass raw string value instead of number
            onChange({ target: { name, value: newVal } }, keyName);
        }
    };

    return (
        <input
            style={{ width }}
            type="text"
            step="any"
            inputMode="decimal"
            name={name}
            value={value ?? ""}
            onChange={handleChange}
        />
    );
};


export function TableComponent({
    scanResult,
    // scanID,
    // subScanID,
    // kernals,
    isEdit,
    setUpdatedKernalCount,
    setUpdatedKernalWeight,
    valuesArray,
    valuesArrayForCount,
    viewUpdatedValues,
    setViewUpdatedValues
}) {
    const [editKarnelCount, setEditKarnelCount] = useState({});
    const [editKarnelCountPercent, setEditKarnelCountPercent] = useState({});
    const [editKarnelWeight, setEditKarnelWeight] = useState({});
    const [editKarnelWeightPercent, setEditKarnelWeightPercent] = useState({});

    const totalCount = Number(scanResult?.kernel_details?.total_kernel_count) || 0;
    const totalWeight = Number(scanResult?.kernel_details?.total_kernel_weight) || 0;
    const manualWeightTotal = Number(scanResult?.weight_value) || totalWeight;


    // --- Setup initial state from valuesArray ---
    useEffect(() => {
        if (!valuesArray) return;

        setEditKarnelWeightPercent(valuesArray);

        const sum = Object.values(valuesArray)
            .map((v) => parseFloat(v) || 0)
            .reduce((acc, v) => acc + v, 0);

        setViewUpdatedValues((prev) => ({
            ...prev,
            updatedKarnelWeightPercentage: sum.toFixed(4)
        }));

        for (const key in valuesArray) {
            const weightVal = ((parseFloat(valuesArray[key]) || 0) * manualWeightTotal) / 100;
            setUpdatedKernalWeight((prev) => ({
                ...prev,
                [key]: Number(weightVal.toFixed(4))
            }));
        }
    }, [valuesArray, manualWeightTotal, setUpdatedKernalWeight]);

    // --- Setup initial state from valuesArrayForCount (for counts directly) ---
    useEffect(() => {
        if (!valuesArrayForCount) return;

        setEditKarnelCount(valuesArrayForCount);

        // Sum of all counts
        const sum = Object.values(valuesArrayForCount)
            .map((v) => parseInt(v) || 0)
            .reduce((acc, v) => acc + v, 0);

        setViewUpdatedValues((prev) => ({
            ...prev,
            updatedKarnelCountTotal: sum, // total count directly
        }));

        // Update kernel count state directly (no percentage conversion needed)
        for (const key in valuesArrayForCount) {
            const countVal = parseInt(valuesArrayForCount[key]) || 0;
            setUpdatedKernalCount((prev) => ({
                ...prev,
                [key]: countVal,
            }));
        }
    }, [valuesArrayForCount, setUpdatedKernalCount]);

    // --- Setup from scanResult ---
    useEffect(() => {
        if (scanResult?.kernel_details?.kernel_counts) {
            const counts = { ...scanResult.kernel_details.kernel_counts };
            setEditKarnelCount(counts);
            setUpdatedKernalCount(counts);

            const percentages = {};
            for (const k in counts) {
                percentages[k] = percent(counts[k], totalCount).replace("%", "");
            }
            setEditKarnelCountPercent(percentages);
        }

        if (scanResult?.kernel_details?.kernel_weights) {
            const weights = { ...scanResult.kernel_details.kernel_weights };

            // Step 1: calculate original percentages based on totalWeight
            const originalPercentages = {};
            for (const k in weights) {
                const perc = (weights[k] / totalWeight) * 100; // keep numeric
                originalPercentages[k] = parseFloat(perc.toFixed(2));
            }

            // Step 2: recalc manual weights from percentage * manualWeightTotal
            const manualWeights = {};
            for (const k in originalPercentages) {
                const perc = originalPercentages[k] || 0;
                manualWeights[k] = parseFloat(((perc / 100) * manualWeightTotal).toFixed(4));
            }

            // Step 3: recalc percentages from new manualWeights (so % matches manualWeightTotal exactly)
            const manualPercentages = {};
            for (const k in manualWeights) {
                manualPercentages[k] = ((manualWeights[k] / manualWeightTotal) * 100).toFixed(2);
            }

            setEditKarnelWeight(manualWeights);
            setUpdatedKernalWeight(manualWeights);
            setEditKarnelWeightPercent(manualPercentages);
        }


    }, [scanResult, isEdit, totalCount, totalWeight, manualWeightTotal, setUpdatedKernalCount, setUpdatedKernalWeight]);

    // --- Update totals whenever edits change ---
    useEffect(() => {
        const countSum = Object.values(editKarnelCount)
            .reduce((s, v) => s + (parseFloat(v) || 0), 0);
        const weightSum = Object.values(editKarnelWeight)
            .reduce((s, v) => s + (parseFloat(v) || 0), 0);

        const countPercentSum = Object.values(editKarnelCountPercent)
            .reduce((s, v) => s + (parseFloat(v) || 0), 0);
        const weightPercentSum = Object.values(editKarnelWeightPercent)
            .reduce((s, v) => s + (parseFloat(v) || 0), 0);

        setViewUpdatedValues({
            updatedKarnelCount: countSum,
            updatedKarnelCountPercentage: countPercentSum,
            updatedKarnelWeight: weightSum,
            updatedKarnelWeightPercentage: weightPercentSum
        });
    }, [
        editKarnelCount,
        editKarnelWeight,
        editKarnelCountPercent,
        editKarnelWeightPercent
    ]);


    // --- Handlers ---
    const onHandleKernalCountAndPercenatage = (e, key) => {
        const { name, value } = e.target;

        if (name === "kernal_count") {
            // store string as-is for typing
            setEditKarnelCount((p) => ({ ...p, [key]: value }));

            const num = parseFloat(value);
            if (!isNaN(num)) {
                setUpdatedKernalCount((p) => ({ ...p, [key]: num }));
                setEditKarnelCountPercent((p) => ({
                    ...p,
                    [key]: ((num * 100) / totalCount).toFixed(2),
                }));
            }
        }

        if (name === "kernal_count_percent") {
            // store raw string
            setEditKarnelCountPercent((p) => ({ ...p, [key]: value }));

            const num = parseFloat(value);
            if (!isNaN(num)) {
                const countVal = Math.round((num * totalCount) / 100);
                setEditKarnelCount((p) => ({ ...p, [key]: countVal.toString() }));
                setUpdatedKernalCount((p) => ({ ...p, [key]: countVal }));
            }
        }
    };

    const onHandleKernalWeightAndPercenatage = (e, key) => {
        const { name, value } = e.target;

        if (name === "kernel_weights") {
            const num = parseFloat(value);
            setEditKarnelWeight((p) => ({ ...p, [key]: value }));
            if (!isNaN(num)) {
                setUpdatedKernalWeight((p) => ({ ...p, [key]: num }));
                setEditKarnelWeightPercent((p) => ({
                    ...p,
                    [key]: ((num * 100) / manualWeightTotal).toFixed(2)
                }));
            }
        }

        if (name === "kernel_weights_percent") {
            setEditKarnelWeightPercent((p) => ({ ...p, [key]: value }));

            const num = parseFloat(value);
            if (!isNaN(num)) {
                const weightVal = (num * manualWeightTotal) / 100;
                const roundedVal = Number(weightVal.toFixed(4))
                setEditKarnelWeight((p) => ({ ...p, [key]: roundedVal }));
                setUpdatedKernalWeight((p) => ({ ...p, [key]: roundedVal }));
            }
        }

    };


    // --- Build headers ---
    const headers = [
        { label: "" },
        { label: "Particulars" },
        { label: "Count" },
        ...(isEdit ? [{ label: "Edit Count" }] : []),
        { label: "Count %" },
        // ...(isEdit ? [{ label: "Edit Count (%)" }] : []),
        ...(totalWeight > 0
            ? [
                { label: "Weight (gm)" },
                // ...(isEdit ? [{ label: "Edit Manual Weight" }] : []),
                { label: "Weight (%)" },
                { label: "Manual Weight" },
                ...(isEdit ? [{ label: "Edit Manual Weight (%)" }] : [])
            ]
            : []),
        // ...(scanResult?.kernel_details?.grading ? [{ label: "Grade" }] : []),
        // ...(scanResult?.kernel_details?.consider_area ? [{ label: "Area (mm2)" }] : []),
        // ...(scanResult?.kernel_details?.consider_length ? [{ label: "Length (mm)" }] : []),
    ];

    // --- Build rows ---
    const rows = [];

    // Totals row
    rows.push([
        "",
        scanResult?.kernel_details?.beautify?.translations?.total_kernel_count,
        totalCount,
        ...(isEdit ? [<p>{viewUpdatedValues.updatedKarnelCount}</p>] : []),
        "100%",
        // ...(isEdit ? [<p>{fmt(viewUpdatedValues.updatedKarnelCountPercentage)}</p>] : []),
        ...(totalWeight > 0
            ? [
                totalWeight,
                // ...(isEdit ? [<p>{fmt(viewUpdatedValues.updatedKarnelWeight)}</p>] : []),
                "100%",
                fmt(viewUpdatedValues.updatedKarnelWeight),
                ...(isEdit ? [<p>{fmt(viewUpdatedValues.updatedKarnelWeightPercentage)}</p>] : [])
            ]
            : []),
        // ...(scanResult?.kernel_details?.grading ? [scanResult?.kernel_details?.overall_score_grading] : []),
        // ...(scanResult?.kernel_details?.consider_area ? [""] : []),
        // ...(scanResult?.kernel_details?.consider_length ? [""] : []),
    ]);

    // Class rows
    scanResult?.kernel_details?.classes.forEach((key) => {
        const weightKey = scanResult?.kernel_details?.kernel_weights || {};
        const count = scanResult?.kernel_details?.kernel_counts[key] || 0;
        const weight = weightKey[key] || 0;

        const row = [
            { cellType: "box", color: scanResult?.processed_image?.encoding_colors[key] },
            `${scanResult?.kernel_details?.beautify?.translations?.kernel_counts[key]} (${scanResult?.processed_image?.encodings[key]})`,
            count,
            ...(isEdit ? [<EditableInput value={editKarnelCount[key]} name="kernal_count" keyName={key} onChange={onHandleKernalCountAndPercenatage} width="100%" />] : []),
            percent(count, totalCount),
            //   ...(isEdit ? [<EditableInput value={editKarnelCountPercent[key]} name="kernal_count_percent" keyName={key} onChange={onHandleKernalCountAndPercenatage} />] : []),
            ...(totalWeight > 0
                ? [
                    fmt(weight),
                    // ...(isEdit ? [<EditableInput value={editKarnelWeight[key]} name="kernel_weights" keyName={key} onChange={onHandleKernalWeightAndPercenatage} width="100%" />] : []),
                    percent(weight, totalWeight),
                    fmt(editKarnelWeight[key]),
                    ...(isEdit ? [<EditableInput value={editKarnelWeightPercent[key]} name="kernel_weights_percent" keyName={key} onChange={onHandleKernalWeightAndPercenatage} />] : [])
                ]
                : []),
            //   ...(scanResult?.kernel_details?.grading ? [scanResult.kernel_details.grading[key]] : []),
            // ...(scanResult?.kernel_details?.consider_area ? [fmt(scanResult.kernel_details.areas[key])] : []),
            // ...(scanResult?.kernel_details?.consider_length ? [fmt(scanResult.kernel_details.kernel_lengths[key])] : []),
        ];
        rows.push(row);
    });

    // Moisture row
    rows.push(["", "Moisture", scanResult?.scan_metadata?.moisture ? `${scanResult.scan_metadata.moisture}%` : "NA"]);

    return (
        <Box>
            <div style={{ marginTop: 20 }}>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead style={{ backgroundColor: "aliceblue", textAlign: "left" }}>
                            <TableRow>
                                {headers.map((h, i) => (
                                    <TableCell key={i}>{h.label}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, i) => (
                                <TableRow key={i}>
                                    {row.map((cell, j) => {
                                        const headerLabel = headers[j]?.label;
                                        const isWeightCol =
                                            headerLabel?.toLowerCase().includes("manual");

                                        return (
                                            <TableCell
                                                key={j}
                                                sx={{
                                                    background: isWeightCol
                                                        ? "linear-gradient(to bottom, #d7e7f5, #a9c9e4)"
                                                        : "inherit",
                                                }}
                                            >
                                                {cell?.cellType === "box" ? (
                                                    <div
                                                        style={{
                                                            backgroundColor: cell.color,
                                                            width: 20,
                                                            height: 20,
                                                        }}
                                                    />
                                                ) : (
                                                    cell
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>

                    </Table>
                </TableContainer>
            </div>
        </Box>
    );
}
